import React, { useState } from 'react';
import {
  Flex,
  Button,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  VStack,
  HStack,
  useDisclosure,
  Grid,
  GridItem,
  Divider,
  Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@chakra-ui/icons';
import { LuSparkles } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import {
  DATE_MODES,
  formatDateRangeDisplay,
  formatDateString,
  parseDateString
} from '../../../utils/dateUtils';

/**
 * The first of the month a YYYY-MM-DD string falls in, read as a local date.
 * `new Date('2025-05-01')` is UTC midnight, which in Colombia is still April 30,
 * so a calendar opened on May showed April (MI-315). Null for an empty value.
 */
const monthOf = (dateStr) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr || '')) return null;
  const date = parseDateString(dateStr);
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Custom Calendar Component built with Chakra UI
 * Supports range selection with highlighted range
 */
const CustomCalendar = ({ viewDate, onViewDateChange, selectedDate, startDate, endDate, onDateSelect, minDate, maxDate }) => {
  const { t } = useTranslation('filters');

  const monthNames = t('dateRange.monthNames', { returnObjects: true });
  const dayNames = t('dateRange.dayNames', { returnObjects: true });

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    // Convert Sunday (0) to 7 for Monday-first week
    return day === 0 ? 6 : day - 1;
  };

  const handlePrevMonth = () => {
    onViewDateChange(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    onViewDateChange(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleDateClick = (day) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    onDateSelect(newDate);
  };

  const isDateDisabled = (day) => {
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    if (minDate && date < new Date(minDate)) return true;
    if (maxDate && date > new Date(maxDate)) return true;
    return false;
  };

  // Helper to parse date string (YYYY-MM-DD) into comparable values
  const parseDateParts = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    return { year: parseInt(parts[0]), month: parseInt(parts[1]) - 1, day: parseInt(parts[2]) };
  };

  const isDateSelected = (day) => {
    const selected = parseDateParts(selectedDate);
    if (!selected) return false;
    return viewDate.getFullYear() === selected.year && 
           viewDate.getMonth() === selected.month && 
           day === selected.day;
  };

  const isStartDate = (day) => {
    const start = parseDateParts(startDate);
    if (!start) return false;
    return viewDate.getFullYear() === start.year && 
           viewDate.getMonth() === start.month && 
           day === start.day;
  };

  const isEndDate = (day) => {
    const end = parseDateParts(endDate);
    if (!end) return false;
    return viewDate.getFullYear() === end.year && 
           viewDate.getMonth() === end.month && 
           day === end.day;
  };

  const isInRange = (day) => {
    const start = parseDateParts(startDate);
    const end = parseDateParts(endDate);
    if (!start || !end) return false;
    
    // Convert to comparable numbers (YYYYMMDD format)
    const dateNum = viewDate.getFullYear() * 10000 + (viewDate.getMonth() + 1) * 100 + day;
    const startNum = start.year * 10000 + (start.month + 1) * 100 + start.day;
    const endNum = end.year * 10000 + (end.month + 1) * 100 + end.day;
    
    return dateNum > startNum && dateNum < endNum;
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  // Build calendar grid
  const days = [];
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  // Days of month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
    <Box p={2} minW="280px">
      {/* Header with month/year navigation */}
      <Flex justify="space-between" align="center" mb={3}>
        <IconButton
          icon={<Text>←</Text>}
          size="sm"
          variant="ghost"
          onClick={handlePrevMonth}
          aria-label={t('dateRange.modal.previousMonth')}
        />
        <Text fontWeight="600" fontSize="md">
          {monthNames[month]} {year}
        </Text>
        <IconButton
          icon={<Text>→</Text>}
          size="sm"
          variant="ghost"
          onClick={handleNextMonth}
          aria-label={t('dateRange.modal.nextMonth')}
        />
      </Flex>

      {/* Day names header */}
      <Grid templateColumns="repeat(7, 1fr)" gap={1} mb={2}>
        {dayNames.map(day => (
          <GridItem key={day} textAlign="center">
            <Text fontSize="xs" color="redesign.text.muted" fontWeight="500">
              {day}
            </Text>
          </GridItem>
        ))}
      </Grid>

      {/* Calendar days */}
      <Grid templateColumns="repeat(7, 1fr)" gap={0}>
        {days.map((day, index) => {
          const isStart = isStartDate(day);
          const isEnd = isEndDate(day);
          const inRange = isInRange(day);
          const isSelected = isDateSelected(day) || isStart || isEnd;
          
          return (
            <GridItem key={index}>
              {day ? (
                <Box
                  bg={inRange ? "redesign.bg.accent" : "transparent"}
                  borderLeftRadius={isStart ? "md" : "none"}
                  borderRightRadius={isEnd ? "md" : "none"}
                >
                  <Button
                    size="sm"
                    variant={isSelected ? "solid" : "ghost"}
                    colorScheme={isSelected ? "blue" : "gray"}
                    isDisabled={isDateDisabled(day)}
                    onClick={() => handleDateClick(day)}
                    w="100%"
                    h="32px"
                    fontSize="sm"
                    bg={inRange && !isSelected ? "redesign.bg.accent" : undefined}
                    // A chosen day keeps the theme's own hover for the blue button.
                    _hover={isSelected ? undefined : { bg: inRange ? "redesign.accent.blue" : "redesign.bg.card" }}
                  >
                    {day}
                  </Button>
                </Box>
              ) : (
                <Box h="32px" />
              )}
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
};

/**
 * Custom Date Range Picker Component
 */
const DateRangePicker = ({ startDate, endDate, onApply, onClose }) => {
  const { t } = useTranslation('filters');
  const [localStart, setLocalStart] = useState(startDate || '');
  const [localEnd, setLocalEnd] = useState(endDate || '');
  const [activeInput, setActiveInput] = useState('start');
  // The month on screen (MI-315). It opens on the range being edited and
  // follows the inputs, a date typed or the other input focused, but not a
  // click on the calendar: picking a start day never jumps to the old end.
  const [viewDate, setViewDate] = useState(() => {
    const today = new Date();
    return monthOf(startDate) || new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const showMonthOf = (dateStr) => {
    const month = monthOf(dateStr);
    if (month) setViewDate(month);
  };

  const handleCalendarSelect = (date) => {
    const dateStr = formatDateString(date);
    if (activeInput === 'start') {
      setLocalStart(dateStr);
      setActiveInput('end');
    } else {
      setLocalEnd(dateStr);
    }
  };

  const handleApply = () => {
    if (localStart && localEnd) {
      // Ensure start is before end
      const start = new Date(localStart);
      const end = new Date(localEnd);
      if (start > end) {
        onApply(localEnd, localStart);
      } else {
        onApply(localStart, localEnd);
      }
      onClose();
    }
  };

  const isValid = localStart && localEnd;

  return (
    <VStack spacing={4} p={2}>
      {/* Date inputs */}
      <HStack spacing={4} w="100%">
        <VStack align="start" flex={1}>
          <Text fontSize="sm" fontWeight="500" color="redesign.text">{t('dateRange.modal.startDate')}</Text>
          <Input
            type="date"
            value={localStart}
            onChange={(e) => { setLocalStart(e.target.value); showMonthOf(e.target.value); }}
            onFocus={() => { setActiveInput('start'); showMonthOf(localStart); }}
            size="sm"
            borderColor={activeInput === 'start' ? 'redesign.accent.blue' : 'redesign.border.strong'}
            borderWidth={activeInput === 'start' ? '2px' : '1px'}
          />
        </VStack>
        <VStack align="start" flex={1}>
          <Text fontSize="sm" fontWeight="500" color="redesign.text">{t('dateRange.modal.endDate')}</Text>
          <Input
            type="date"
            value={localEnd}
            onChange={(e) => { setLocalEnd(e.target.value); showMonthOf(e.target.value); }}
            onFocus={() => { setActiveInput('end'); showMonthOf(localEnd); }}
            size="sm"
            borderColor={activeInput === 'end' ? 'redesign.accent.blue' : 'redesign.border.strong'}
            borderWidth={activeInput === 'end' ? '2px' : '1px'}
          />
        </VStack>
      </HStack>

      {/* Calendar */}
      <Box borderWidth="1px" borderRadius="md" borderColor="redesign.border">
        <CustomCalendar
          viewDate={viewDate}
          onViewDateChange={setViewDate}
          selectedDate={activeInput === 'start' ? localStart : localEnd}
          startDate={localStart}
          endDate={localEnd}
          onDateSelect={handleCalendarSelect}
        />
      </Box>

      {/* Actions */}
      <HStack spacing={2} w="100%" justify="flex-end">
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('dateRange.modal.cancel')}
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={handleApply}
          isDisabled={!isValid}
        >
          {t('dateRange.modal.apply')}
        </Button>
      </HStack>
    </VStack>
  );
};

/**
 * Mode i18n keys (resolved at render via useTranslation)
 */
const MODE_KEYS = {
  [DATE_MODES.MONTH]: 'month',
  [DATE_MODES.YEAR]: 'year',
  [DATE_MODES.WEEK]: 'week',
  [DATE_MODES.CUSTOM]: 'custom',
  [DATE_MODES.ALL]: 'all'
};

/**
 * DateRangeSelector - Advanced date range selection component
 * 
 * Features:
 * - Mode switching (Month/Week/Year/Custom)
 * - Navigation arrows for prev/next period
 * - Today button for quick jump
 * - Custom date range picker
 * 
 * @param {Object} dateRange - Current date range object
 * @param {Function} onNavigate - Callback for prev/next navigation
 * @param {Function} onModeChange - Callback when mode changes
 * @param {Function} onCustomRange - Callback for custom date range
 * @param {Function} onToday - Callback for today button
 */
export function DateRangeSelector({
  dateRange,
  // Optional: a period written in words. Without it the box is not offered.
  onParsePeriod,
  onNavigate,
  onModeChange,
  onCustomRange,
  onToday
}) {
  const { t } = useTranslation('filters');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [periodText, setPeriodText] = useState('');
  const [periodState, setPeriodState] = useState('idle'); // idle | pending | failed

  const submitPeriod = async () => {
    const text = periodText.trim();
    if (!text || !onParsePeriod) return;
    setPeriodState('pending');
    try {
      const ok = await onParsePeriod(text);
      setPeriodState(ok === false ? 'failed' : 'idle');
      if (ok !== false) setPeriodText('');
    } catch (err) {
      setPeriodState('failed');
    }
  };

  const currentMode = dateRange?.mode || DATE_MODES.MONTH;
  const isCustomMode = currentMode === DATE_MODES.CUSTOM;
  const isAllMode = currentMode === DATE_MODES.ALL;

  const handleModeSelect = (mode) => {
    if (mode === DATE_MODES.CUSTOM) {
      onOpen();
    } else {
      onModeChange(mode);
    }
  };

  const handleCustomApply = (start, end) => {
    onCustomRange(parseDateString(start), parseDateString(end));
  };

  return (
    <Flex
      align="center"
      gap={{ base: 2, md: 3 }}
    >
      {/* Mode Selector Dropdown - styled like Filter button */}
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon boxSize={3} />}
          size="sm"
          // 28px, the height of every control beside it in the toolbar.
          h="28px"
          px="10px"
          borderRadius="6px"
          fontWeight="500"
          bg="redesign.bg.surface"
          color="redesign.text"
          border="1px solid"
          borderColor="redesign.border.strong"
          _hover={{ bg: "redesign.bg.hover", borderColor: "redesign.text.muted" }}
          _active={{ bg: "redesign.bg.card" }}
          minW={{ base: "80px", md: "92px" }}
          fontSize={{ base: "xs", md: "13px" }}
        >
          {t(`dateRange.modes.${MODE_KEYS[currentMode]}`)}
        </MenuButton>
        <MenuList zIndex={10} py={1} minW="100px">
          {Object.entries(MODE_KEYS).map(([mode, modeKey]) => (
            <MenuItem
              key={mode}
              onClick={() => handleModeSelect(mode)}
              fontWeight={mode === currentMode ? '600' : '400'}
              bg={mode === currentMode ? 'redesign.bg.accent' : 'transparent'}
              py={1}
              minH="28px"
              fontSize="sm"
            >
              {t(`dateRange.modes.${modeKey}`)}
            </MenuItem>
          ))}

          {/* A period in words — "June 2024", "last week of december". The
              same parser the AI filter uses; only its date range is applied. */}
          {onParsePeriod && (
            <>
              <Divider my={1} />
              <Flex align="center" gap={1.5} px={3} py={1.5}>
                {periodState === 'pending'
                  ? <Spinner size="xs" color="redesign.accent.blue" flexShrink={0} />
                  : <Box as={LuSparkles} boxSize="13px" color="redesign.accent.blue" flexShrink={0} />}
                <Input
                  variant="unstyled"
                  size="xs"
                  fontSize="sm"
                  placeholder={t('dateRange.aiPeriod.placeholder')}
                  value={periodText}
                  onChange={(e) => { setPeriodText(e.target.value); setPeriodState('idle'); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); submitPeriod(); }
                    // The menu would otherwise read typing as its own
                    // first-letter navigation and steal the caret.
                    e.stopPropagation();
                  }}
                  color="redesign.text"
                  _placeholder={{ color: 'redesign.text.muted' }}
                />
              </Flex>
              {periodState === 'failed' && (
                <Text px={3} pb={1} fontSize="xs" color="redesign.accent.red">
                  {t('dateRange.aiPeriod.failed')}
                </Text>
              )}
            </>
          )}
        </MenuList>
      </Menu>

      {/* Navigation - only show for modes with date ranges */}
      {!isCustomMode && !isAllMode && (
        <Flex align="center" gap={1}>
          <IconButton
            icon={<ChevronLeftIcon boxSize={4} />}
            size="sm"
            onClick={() => onNavigate('prev')}
            aria-label={t('dateRange.aria.previousPeriod')}
            variant="ghost"
            color="redesign.text.muted"
            _hover={{ bg: "redesign.bg.card", color: "redesign.text" }}
            minW="28px"
            h="28px"
          />

          <Text
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight="600"
            color="redesign.text"
            textAlign="center"
            whiteSpace="nowrap"
          >
            {formatDateRangeDisplay(dateRange)}
          </Text>

          <IconButton
            icon={<ChevronRightIcon boxSize={4} />}
            size="sm"
            onClick={() => onNavigate('next')}
            aria-label={t('dateRange.aria.nextPeriod')}
            variant="ghost"
            color="redesign.text.muted"
            _hover={{ bg: "redesign.bg.card", color: "redesign.text" }}
            minW="28px"
            h="28px"
          />
        </Flex>
      )}

      {/* Custom mode display with edit button */}
      {isCustomMode && (
        <Button
          variant="outline"
          size="sm"
          borderColor="redesign.border.strong"
          onClick={onOpen}
          leftIcon={<CalendarIcon boxSize={3} />}
          fontWeight="600"
          fontSize={{ base: "xs", md: "sm" }}
        >
          {formatDateRangeDisplay(dateRange)}
        </Button>
      )}

      {/* Custom Date Range Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('dateRange.modal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <DateRangePicker
              startDate={dateRange?.start_date}
              endDate={dateRange?.end_date}
              onApply={handleCustomApply}
              onClose={onClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

    </Flex>
  );
}

export default DateRangeSelector;
